import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import { PostAdd as PostAddIcon, NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Card as CardType } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { useAdmin } from '../contexts/AdminContext';

const CardDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, saveCard, isCardSaved, removeSavedCard } = useAuth();
  const { mode } = useTheme();
  const { addToCart, isInCart } = useCart();
  const { isAdmin } = useAdmin();
  const [card, setCard] = useState<CardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const isSaved = user && card ? isCardSaved(card.id) : false;

  useEffect(() => {
    const loadCard = async () => {
      try {
        setLoading(true);
        // In a real app, this would be an API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Load cards from localStorage
        const savedCards = localStorage.getItem('adminCards');
        if (savedCards) {
          const allCards = JSON.parse(savedCards);
          const foundCard = allCards.find((c: CardType) => c.id === id);
          
          if (foundCard) {
            setCard(foundCard);
            setCurrentImageIndex(0); // Reset to first image
            setError(null);
          } else {
            setError('Card not found');
          }
        } else {
          setError('Card not found');
        }
      } catch (err) {
        setError('Failed to load card details');
      } finally {
        setLoading(false);
      }
    };

    loadCard();
  }, [id]);

  const handleSaveCard = () => {
    if (!card) return;
    if (isSaved) {
      removeSavedCard(card.id);
    } else {
      saveCard(card.id);
    }
  };

  // Get all available images for the card
  const getAllImages = (card: CardType): string[] => {
    const images = [card.imageUrl];
    if (card.additionalImageUrls && card.additionalImageUrls.length > 0) {
      images.push(...card.additionalImageUrls);
    }
    return images.filter(url => url && url.trim() !== '');
  };

  const handlePreviousImage = () => {
    if (!card) return;
    const images = getAllImages(card);
    setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    if (!card) return;
    const images = getAllImages(card);
    setCurrentImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Fallback to placeholder if image fails to load
    e.currentTarget.src = '/placeholder-card.jpg';
  };

  const [showPostDialog, setShowPostDialog] = useState(false);
  const [postOptions, setPostOptions] = useState({
    postToCollection: true,
    postToHomepage: false,
  });

  const handlePostToGoatCollection = () => {
    if (!card) return;
    
    // Reset post options
    setPostOptions({
      postToCollection: true,
      postToHomepage: false,
    });
    setShowPostDialog(true);
  };

  const confirmPostToGoatCollection = () => {
    if (!card) return;
    
    try {
      // Load current cards from localStorage
      const savedCards = localStorage.getItem('adminCards');
      if (savedCards) {
        const allCards = JSON.parse(savedCards);
        
        // Find and update the card with posting options
        const updatedCards = allCards.map((c: CardType) => 
          c.id === card.id ? { 
            ...c, 
            isPosted: postOptions.postToCollection,
            isHomepage: postOptions.postToHomepage
          } : c
        );
        
        // Save back to localStorage
        localStorage.setItem('adminCards', JSON.stringify(updatedCards));
        
        // Update the current card state
        setCard({ 
          ...card, 
          isPosted: postOptions.postToCollection,
          isHomepage: postOptions.postToHomepage
        });
        
        // Show success message based on what was posted
        let message = '';
        if (postOptions.postToCollection && postOptions.postToHomepage) {
          message = 'Card posted to Goat Collection and Homepage successfully!';
        } else if (postOptions.postToCollection) {
          message = 'Card posted to Goat Collection successfully!';
        } else if (postOptions.postToHomepage) {
          message = 'Card posted to Homepage successfully!';
        }
        
        alert(message);
      }
    } catch (error) {
      console.error('Error posting card:', error);
      alert('Error posting card. Please try again.');
    }
    
    setShowPostDialog(false);
  };

  const cancelPostToGoatCollection = () => {
    setShowPostDialog(false);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getCardDescription = (card: CardType) => {
    // Generate unique fun descriptions based on player/card
    const descriptions = [
      `This iconic ${card.year} ${card.brand} ${card.player} card showcases one of the greatest talents in ${card.sport}. A must-have for serious collectors!`,
      `A piece of sports history! The ${card.year} ${card.player} ${card.brand} card captures a legendary moment in ${card.sport}.`,
      `Collectors dream! This ${card.player} card from ${card.year} is in ${card.condition} condition and represents a pivotal era for the ${card.team}.`,
      `Rare find! The ${card.year} ${card.brand} series featuring ${card.player} is highly sought after by ${card.sport} enthusiasts worldwide.`,
      `This ${card.player} card is a gem from the golden era of ${card.sport}. The ${card.year} ${card.brand} edition shows why ${card.player} became a legend with the ${card.team}.`
    ];
    
    // Use player name to generate a consistent but seemingly random description
    const nameSum = card.player.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return descriptions[nameSum % descriptions.length];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !card) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error || 'Card not found'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/cards')}>
          Back to Cards
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
      </Box>

      <Paper sx={{ overflow: 'hidden', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Card Image */}
                    <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              bgcolor: 'background.paper',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              borderRight: { xs: 'none', md: '1px solid rgba(255,255,255,0.1)' },
              position: 'relative'
            }}
          >
            {/* Image Gallery */}
            {(() => {
              const images = getAllImages(card);
              const currentImage = images[currentImageIndex];
              
              return (
                <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {/* Main Image */}
                  <Box
                    component="img"
                    src={currentImage}
                    alt={`${card.title} - Image ${currentImageIndex + 1}`}
                    onError={handleImageError}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '500px',
                      objectFit: 'contain',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                      borderRadius: 1,
                      transition: 'opacity 0.3s ease-in-out'
                    }}
                  />
                  
                  {/* Navigation Arrows - Only show if there are multiple images */}
                  {images.length > 1 && (
                    <>
                      {/* Previous Button */}
                      <IconButton
                        onClick={handlePreviousImage}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.9)',
                          },
                          zIndex: 2
                        }}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                      
                      {/* Next Button */}
                      <IconButton
                        onClick={handleNextImage}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.9)',
                          },
                          zIndex: 2
                        }}
                      >
                        <NavigateNextIcon />
                      </IconButton>
                      
                      {/* Image Counter */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}
                      >
                        {currentImageIndex + 1} of {images.length}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })()}
          </Box>

          {/* Card Details */}
          <Box sx={{ width: { xs: '100%', md: '50%' }, p: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {card.player}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={card.team} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={card.year.toString()} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={card.brand} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={card.isGraded ? card.grade : card.condition} 
                size="small" 
                color={card.isGraded ? 'primary' : 'default'} 
                variant={card.isGraded ? 'filled' : 'outlined'} 
              />
              {card.isGraded && (
                <Chip label="Graded" size="small" color="primary" />
              )}
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ 
              mb: 3,
              color: mode === 'light' ? '#000000' : '#FFFFFF'
            }}>
              {formatPrice(card.price)}
            </Typography>

            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
              {getCardDescription(card)}
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
              {card.description}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mt: 4, flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  fullWidth
                  onClick={() => {
                    if (card) {
                      addToCart(card);
                      navigate('/cart');
                    }
                  }}
                  disabled={!card}
                  sx={{ 
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                >
                  Buy Now
                </Button>
                
                {user && (
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={handleSaveCard}
                    sx={{ 
                      minWidth: '120px',
                      py: 1.5
                    }}
                  >
                    {isSaved ? 'Saved' : 'Save'}
                  </Button>
                )}
              </Box>
              
              {/* Post to Goat Collection Button - Only visible to admins */}
              {isAdmin && !card.isPosted && (
                <Button 
                  variant="contained" 
                  size="large" 
                  fullWidth
                  onClick={handlePostToGoatCollection}
                  sx={{ 
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem',
                    backgroundColor: 'success.main',
                    '&:hover': {
                      backgroundColor: 'success.dark',
                    }
                  }}
                >
                  Post to Goat Collection
                </Button>
              )}
              
              {/* Add to Cart Button */}
              <Button 
                variant="contained" 
                size="large" 
                fullWidth
                startIcon={<ShoppingCartIcon />}
                onClick={() => {
                  if (card) {
                    addToCart(card);
                  }
                }}
                disabled={!card}
                sx={{ 
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  backgroundColor: isInCart(card?.id || '') ? 'success.main' : 'primary.main',
                  '&:hover': {
                    backgroundColor: isInCart(card?.id || '') ? 'success.dark' : 'primary.dark',
                  }
                }}
              >
                {isInCart(card?.id || '') ? 'Added to Cart' : 'Add to Cart'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Post Options Dialog */}
      <Dialog
        open={showPostDialog}
        onClose={cancelPostToGoatCollection}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PostAddIcon color="primary" />
            Post Card
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
            Choose where you want to post this card:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={postOptions.postToCollection}
                  onChange={(e) => setPostOptions(prev => ({ 
                    ...prev, 
                    postToCollection: e.target.checked 
                  }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Post to Goat Collection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add this card to the main Goat Collection
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={postOptions.postToHomepage}
                  onChange={(e) => setPostOptions(prev => ({ 
                    ...prev, 
                    postToHomepage: e.target.checked 
                  }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Post to Homepage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Feature this card on the main homepage
                  </Typography>
                </Box>
              }
            />
          </Box>

          {!postOptions.postToCollection && !postOptions.postToHomepage && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one option to post the card.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPostToGoatCollection} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={confirmPostToGoatCollection} 
            variant="contained" 
            disabled={!postOptions.postToCollection && !postOptions.postToHomepage}
            startIcon={<PostAddIcon />}
            sx={{
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              },
            }}
          >
            Post Card
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CardDetailPage; 